import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HelpOutline as OtherIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import UserSupportHistory from '../../models/userSupportHistory';

const UserSupportHistoryComponent = ({ userId, currentUser }) => {
  const [supportHistory, setSupportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    support_type: '',
    title: '',
    description: '',
    resolution: 'PENDING'
  });
  const [formErrors, setFormErrors] = useState({});

  const supportTypeIcons = {
    PHONE_CALL: <PhoneIcon />,
    EMAIL: <EmailIcon />,
    CHAT: <ChatIcon />,
    IN_PERSON: <PersonIcon />,
    OTHER: <OtherIcon />
  };

  const supportTypeLabels = {
    PHONE_CALL: 'Gọi điện',
    EMAIL: 'Email',
    CHAT: 'Chat',
    IN_PERSON: 'Trực tiếp',
    OTHER: 'Khác'
  };

  const resolutionColors = {
    RESOLVED: 'success',
    PENDING: 'warning',
    ESCALATED: 'error',
    CANCELLED: 'default'
  };

  const resolutionLabels = {
    RESOLVED: 'Đã giải quyết',
    PENDING: 'Đang xử lý',
    ESCALATED: 'Chuyển lên',
    CANCELLED: 'Đã hủy'
  };

  useEffect(() => {
    if (userId) {
      fetchSupportHistory();
    }
  }, [userId]);

  const fetchSupportHistory = async () => {
    try {
      setLoading(true);
      const result = await UserSupportHistory.getByUserId(userId);
      setSupportHistory(result.data || []);
    } catch (error) {
      console.error('Error fetching support history:', error);
      toast.error('Không thể tải lịch sử hỗ trợ');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.support_type) errors.support_type = 'Vui lòng chọn loại hỗ trợ';
    if (!formData.title) errors.title = 'Vui lòng nhập tiêu đề';
    if (!formData.resolution) errors.resolution = 'Vui lòng chọn trạng thái';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddEntry = () => {
    setEditingEntry(null);
    setFormData({
      support_type: '',
      title: '',
      description: '',
      resolution: 'PENDING'
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      support_type: entry.support_type,
      title: entry.title,
      description: entry.description || '',
      resolution: entry.resolution
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDeleteClick = (entry) => {
    setDeletingEntry(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingEntry) return;
    
    try {
      const entry = new UserSupportHistory(deletingEntry);
      const result = await entry.delete();
      
      if (result.success) {
        toast.success(result.message);
        fetchSupportHistory();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error deleting support entry:', error);
      toast.error('Đã xảy ra lỗi khi xóa');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingEntry(null);
    }
  };

  const handleSaveEntry = async () => {
    if (!validateForm()) return;

    try {
      if (editingEntry) {
        const entry = new UserSupportHistory({
          ...editingEntry,
          ...formData
        });
        const result = await entry.update();
        
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } else {
        const entry = new UserSupportHistory({
          ...formData,
          user_id: userId
        });
        const result = await entry.save();
        
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      }
      
      setDialogOpen(false);
      fetchSupportHistory();
    } catch (error) {
      console.error('Error saving support entry:', error);
      toast.error('Đã xảy ra lỗi khi lưu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('vi-VN', {
      timeZone: 'Asia/Ho_Chi_Minh',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography>Đang tải lịch sử hỗ trợ...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" color="primary">
              Lịch sử hỗ trợ khách hàng
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              size="small"
            >
              Thêm hỗ trợ
            </Button>
          </Box>

          {supportHistory.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="body1" color="text.secondary">
                Chưa có lịch sử hỗ trợ nào
              </Typography>
            </Paper>
          ) : (
            <List sx={{ width: '100%' }}>
              {supportHistory.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      '&:hover': { bgcolor: '#f5f5f5' },
                      py: 2
                    }}
                  >
                    <ListItemIcon sx={{ mt: 1 }}>
                      {supportTypeIcons[entry.support_type]}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {entry.title}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                              <Chip
                                size="small"
                                label={resolutionLabels[entry.resolution]}
                                color={resolutionColors[entry.resolution]}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {supportTypeLabels[entry.support_type]}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditEntry(entry)}
                              title="Chỉnh sửa"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(entry)}
                              title="Xóa"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          {entry.description && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {entry.description}
                            </Typography>
                          )}
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(entry.created_at)}
                              {entry.handler && ` - ${entry.handler.name}`}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Chỉnh sửa hỗ trợ' : 'Thêm hỗ trợ mới'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth error={!!formErrors.support_type}>
              <InputLabel>Loại hỗ trợ *</InputLabel>
              <Select
                value={formData.support_type}
                onChange={(e) => setFormData({...formData, support_type: e.target.value})}
                label="Loại hỗ trợ *"
              >
                <MenuItem value="PHONE_CALL">Gọi điện</MenuItem>
                <MenuItem value="EMAIL">Email</MenuItem>
                <MenuItem value="CHAT">Chat</MenuItem>
                <MenuItem value="IN_PERSON">Trực tiếp</MenuItem>
                <MenuItem value="OTHER">Khác</MenuItem>
              </Select>
              {formErrors.support_type && (
                <Typography variant="caption" color="error">{formErrors.support_type}</Typography>
              )}
            </FormControl>
            
            <TextField
              fullWidth
              label="Tiêu đề *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              error={!!formErrors.title}
              helperText={formErrors.title}
            />
            
            <TextField
              fullWidth
              label="Mô tả chi tiết"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
            
            <FormControl fullWidth error={!!formErrors.resolution}>
              <InputLabel>Trạng thái *</InputLabel>
              <Select
                value={formData.resolution}
                onChange={(e) => setFormData({...formData, resolution: e.target.value})}
                label="Trạng thái *"
              >
                <MenuItem value="RESOLVED">Đã giải quyết</MenuItem>
                <MenuItem value="PENDING">Đang xử lý</MenuItem>
                <MenuItem value="ESCALATED">Chuyển lên</MenuItem>
                <MenuItem value="CANCELLED">Đã hủy</MenuItem>
              </Select>
              {formErrors.resolution && (
                <Typography variant="caption" color="error">{formErrors.resolution}</Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleSaveEntry} variant="contained">
            {editingEntry ? 'Cập nhật' : 'Thêm mới'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa lịch sử hỗ trợ "{deletingEntry?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserSupportHistoryComponent;